import React, { createContext, useContext, useReducer, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import Modals from '@components/modals'
import { addToDataLayer_nextgen } from '@helpers/google-tag-manager'
import { getURLParam } from '@helpers/general'
import { reducer, actions } from './reducer'

/**
 * @typedef {import('./reducer').ModalActions} ModalActions
 */

/**
 * @typedef {import('./reducer').ModalState} ModalState
 */

/**
 * @typedef {Object} ContextActions
 * @property {() => void} createAccount triggers Create Account modal
 * @property {() => void} login triggers Login modal
 * @property {() => void} logout triggers Logout modal
 * @property {() => void} forgotPassword triggers forgot password modal
 * @property {() => void} resetPassword triggers reset password modal
 * @property {() => void} emailSent triggers forgot email sent modal
 * @property {() => void} emailVerification triggers EmailVerification modal
 * @property {() => void} genericModal triggers Generic modal
 * @property {() => void} close
 */

/**
 * @typedef {Object} ModalsContext
 * @property {ContextActions} actions Available actions in the accounts context
 * @property {ModalState} modal state
 */

/**
 * Account Context
 * @type {ModalsContext}
 * */
const initialContextValue = {
  actions,
  modal: {},
}

const ModalsContext = createContext(initialContextValue)

export const ModalsContextProvider = ({ children }) => {
  const [modal, dispatch] = useReducer(reducer, {})

  const contextActions = useMemo(
    () => ({
      logout: () => {
        addToDataLayer_nextgen('account_modal_view', { account_modal_name: 'Log Out' })
        dispatch({ type: actions.logout })
      },
      login: () => {
        addToDataLayer_nextgen('account_modal_view', { account_modal_name: 'Log In' })
        dispatch({ type: actions.login })
      },
      createAccount: () => {
        addToDataLayer_nextgen('account_modal_view', {
          account_modal_name: 'Create Account',
        })
        dispatch({ type: actions.createAccount })
      },
      forgotPassword: () => {
        addToDataLayer_nextgen('account_modal_view', {
          account_modal_name: 'Forgot Password',
        })
        dispatch({ type: actions.forgotPassword })
      },
      resetPassword: () => {
        addToDataLayer_nextgen('account_modal_view', {
          account_modal_name: 'Create New Password',
        })
        dispatch({ type: actions.resetPassword })
      },
      emailSent: () => {
        addToDataLayer_nextgen('account_modal_view', {
          account_modal_name: 'Check your email',
        })
        dispatch({ type: actions.emailSent })
      },
      emailVerification: () => {
        addToDataLayer_nextgen('account_modal_view', {
          account_modal_name: 'Email Verification',
        })
        dispatch({ type: actions.emailVerification })
      },
      genericModal: ({ data, overrideState }) => {
        addToDataLayer_nextgen('generic_modal_view')
        dispatch({ type: actions.genericModal, data, overrideState })
      },
      close: () => dispatch({ type: actions.close }),
    }),
    [dispatch],
  )

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      const queryMode = getURLParam('mode')
      const resetPasswordToken = getURLParam('oobCode')
      if (queryMode === 'resetPassword' && resetPasswordToken) {
        dispatch({ type: actions.resetPassword })
      } else if (queryMode === 'login') {
        dispatch({ type: actions.login })
      }
    }
  }, [])

  /**
   * Account Context
   * @type {ModalsContext}
   * */
  const context = {
    actions: contextActions,
    modal,
  }

  return (
    <ModalsContext.Provider value={context}>
      <Modals />
      {children}
    </ModalsContext.Provider>
  )
}

ModalsContextProvider.propTypes = {
  children: PropTypes.node,
}

/**
 * @returns {ModalsContext}
 */
export const useModalsContext = () => useContext(ModalsContext)
